import { z } from 'zod'
import { calendarDateSchema } from '../validators/calendar-date.validator.js'
import { moneyAmountSchema } from '../validators/money.validator.js'
import { uuidSchema } from '../validators/uuid.validator.js'
import { seasonSchema } from '../enums/season.enum.js'

/**
 * Work an accepted proposal estimated for a season that is coming up, waiting
 * for the office to rebid it (docs/seasonal-work.md, "After acceptance").
 *
 * One per proposal and season: the sites it covers are rebid together, as
 * they were bid together. On the "Coming up" list from the lead time before
 * the season starts until somebody starts the rebid or dismisses it.
 */
export const seasonReminderDtoSchema = z.object({
  proposalId: uuidSchema,
  /** The accepted version whose Future work this is. */
  versionId: uuidSchema,
  proposalTitle: z.string(),
  clientId: uuidSchema,
  clientName: z.string(),
  ownerName: z.string().nullable(),
  season: seasonSchema,
  /** The day the season starts, `YYYY-MM-DD`: the earliest across its sites. */
  startsOn: calendarDateSchema,
  /** The sites with work that season. */
  sites: z.array(z.object({ siteId: uuidSchema, siteName: z.string() })),
  /** What the client was shown for it, before tax. Null when nothing priced. */
  estimate: moneyAmountSchema.nullable(),
  /** When the owner was emailed about it; null until the daily run does. */
  notifiedAt: z.string().datetime().nullable(),
})

export type SeasonReminderDto = z.infer<typeof seasonReminderDtoSchema>

/** What starting a rebid made: the new draft proposal to open. */
export const seasonRebidDtoSchema = z.object({
  proposalId: uuidSchema,
})

export type SeasonRebidDto = z.infer<typeof seasonRebidDtoSchema>

/** What one daily run did, for the scheduler's log. */
export const seasonReminderRunDtoSchema = z.object({
  /** Reminders emailed to their proposal's owner. */
  emailed: z.number().int().nonnegative(),
  /** Reminders with nobody to email, or no mail provider: logged instead. */
  logged: z.number().int().nonnegative(),
})

export type SeasonReminderRunDto = z.infer<typeof seasonReminderRunDtoSchema>
